import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEbookUrl from '../hooks/useEbookUrl';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { searchPlugin } from '@react-pdf-viewer/search';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';

interface TocItem {
    title: string;
    pageIndex: number;
    children: TocItem[];
}

const PdfViewer: React.FC = () => {
    const { type, id } = useParams<{ type: string; id: string }>();
    const { i18n } = useTranslation();
    const isAr = i18n.language === 'ar';
    const [tocItems, setTocItems] = useState<TocItem[]>([]);
    const [showToc, setShowToc] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);

    // Lien signé délivré par l'API (abonnés payants) — #101 : essai gratuit → abonnement
    const { url: pdfUrl, status } = useEbookUrl(type, id, isAr ? 'ar' : 'fr');

    const title = isAr ? 'الكتاب الالكتروني' : 'eBook';

    // Load TOC once from PDF outline via pdfjs
    useEffect(() => {
        if (!pdfUrl) return;

        const loadOutline = async () => {
            try {
                const pdfjsLib = await import('pdfjs-dist');
                pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
                const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
                const outline = await pdf.getOutline();
                if (!outline) return;

                const resolveItem = async (item: any): Promise<TocItem> => {
                    let pageIndex = 0;
                    if (item.dest) {
                        try {
                            const dest = typeof item.dest === 'string'
                                ? await pdf.getDestination(item.dest)
                                : item.dest;
                            if (dest && dest[0]) {
                                const ref = dest[0];
                                pageIndex = await pdf.getPageIndex(ref);
                            }
                        } catch {}
                    }
                    const children = item.items
                        ? await Promise.all(item.items.map(resolveItem))
                        : [];
                    return { title: item.title, pageIndex, children };
                };

                const items = await Promise.all(outline.map(resolveItem));
                setTocItems(items);
            } catch {}
        };
        loadOutline();
    }, [pdfUrl]);

    const searchPluginInstance = searchPlugin({
        onHighlightKeyword: () => {
            setTimeout(() => {
                const viewer = document.querySelector('.rpv-core__inner-pages');
                if (viewer) {
                    const count = viewer.querySelectorAll('.rpv-search__highlight').length;
                    setMatchCount(count);
                }
            }, 100);
        },
    });
    const { highlight, clearHighlights, jumpToNextMatch, jumpToPreviousMatch } = searchPluginInstance;
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { jumpToPage } = pageNavigationPluginInstance;
    const [searchText, setSearchText] = useState('');
    const [matchCount, setMatchCount] = useState(0);

    const handleSearch = (text: string) => {
        if (text.trim()) {
            setMatchCount(0);
            highlight({ keyword: text });
            setTimeout(() => jumpToNextMatch(), 300);
        } else {
            clearHighlights();
            setMatchCount(0);
        }
    };

    const handleNext = () => jumpToNextMatch();
    const handlePrev = () => jumpToPreviousMatch();

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: () => [],
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        CurrentPageInput,
                        GoToNextPage,
                        GoToPreviousPage,
                        NumberOfPages,
                        ZoomIn,
                        ZoomOut,
                    } = slots;

                    const handlePrint = () => {
                        if (!pdfUrl) return;
                        const iframe = document.createElement('iframe');
                        iframe.style.position = 'fixed';
                        iframe.style.top = '-10000px';
                        iframe.style.left = '-10000px';
                        iframe.style.width = '1px';
                        iframe.style.height = '1px';
                        iframe.src = pdfUrl;
                        document.body.appendChild(iframe);
                        iframe.onload = () => {
                            try {
                                iframe.contentWindow?.focus();
                                iframe.contentWindow?.print();
                            } catch (e) {
                                window.open(pdfUrl, '_blank');
                            }
                            setTimeout(() => document.body.removeChild(iframe), 60000);
                        };
                    };

                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                background: '#6B0F1A',
                                padding: '0 8px',
                                height: '44px',
                                gap: '4px',
                            }}
                        >
                            {/* Title */}
                            <div style={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '14px',
                                padding: '0 12px 0 4px',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}>
                                <i className="bi bi-book"></i>
                                {title}
                            </div>

                            <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', height: '24px', margin: '0 4px' }}></div>

                            {/* Navigation */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <GoToPreviousPage />
                                <CurrentPageInput />
                                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', padding: '0 4px' }}>
                                    / <NumberOfPages />
                                </span>
                                <GoToNextPage />
                            </div>

                            <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', height: '24px', margin: '0 4px' }}></div>

                            {/* Zoom */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <ZoomOut />
                                <ZoomIn />
                            </div>

                            <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', height: '24px', margin: '0 4px' }}></div>

                            {/* TOC Toggle */}
                            {tocItems.length > 0 && (
                                <button
                                    onClick={() => setShowToc(prev => !prev)}
                                    style={{
                                        background: showToc ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: '#fff', padding: '5px 10px', borderRadius: '4px', fontSize: '13px',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={isAr ? 'فهرس المحتويات' : 'Table des matières'}
                                >
                                    <i className="bi bi-list-ul"></i>
                                </button>
                            )}

                            {/* Spacer */}
                            <div style={{ flex: 1 }}></div>

                            {/* Inline Search */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '4px',
                                background: 'rgba(0,0,0,0.25)', borderRadius: '4px', padding: '2px 8px',
                            }}>
                                <i className="bi bi-search" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}></i>
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    placeholder={isAr ? 'بحث...' : 'Rechercher...'}
                                    style={{
                                        background: 'transparent', border: 'none', color: 'white',
                                        padding: '4px', width: '120px', outline: 'none', fontSize: '13px',
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            if (e.shiftKey) { handlePrev(); } else { handleSearch(searchText); }
                                        }
                                        if (e.key === 'Escape') { setSearchText(''); clearHighlights(); setMatchCount(0); }
                                    }}
                                />
                                {matchCount > 0 && (
                                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                        {matchCount} {isAr ? 'نتيجة' : 'résultat(s)'}
                                    </span>
                                )}
                                {searchText && matchCount > 0 && (
                                    <>
                                        <span
                                            style={{ color: 'white', cursor: 'pointer', fontSize: '14px', padding: '0 2px' }}
                                            onClick={handlePrev}
                                            title={isAr ? 'السابق' : 'Précédent'}
                                        >
                                            ▲
                                        </span>
                                        <span
                                            style={{ color: 'white', cursor: 'pointer', fontSize: '14px', padding: '0 2px' }}
                                            onClick={handleNext}
                                            title={isAr ? 'التالي' : 'Suivant'}
                                        >
                                            ▼
                                        </span>
                                    </>
                                )}
                                {searchText && (
                                    <span
                                        style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '12px', padding: '0 2px' }}
                                        onClick={() => { setSearchText(''); clearHighlights(); setMatchCount(0); }}
                                    >
                                        ✕
                                    </span>
                                )}
                            </div>

                            {/* Print */}
                            <button
                                className="rpv-core__minimal-button"
                                onClick={handlePrint}
                                title={isAr ? 'طباعة' : 'Imprimer'}
                                style={{ color: 'white', padding: '5px 10px', cursor: 'pointer' }}
                            >
                                <i className="bi bi-printer"></i>
                            </button>
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });

    // Render TOC items recursively
    const renderTocItems = (items: TocItem[], depth: number = 0) => (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map((item, i) => (
                <li key={`${depth}-${i}`}>
                    <div
                        onClick={() => {
                            jumpToPage(item.pageIndex);
                            setCurrentPage(item.pageIndex);
                        }}
                        style={{
                            padding: '6px 12px',
                            paddingLeft: `${12 + depth * 16}px`,
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '13px',
                            direction: isAr ? 'rtl' : 'ltr',
                            textAlign: isAr ? 'right' : 'left',
                            background: currentPage === item.pageIndex ? 'rgba(255,255,255,0.15)' : 'transparent',
                            borderRadius: '2px',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                        onMouseLeave={e => (e.currentTarget.style.background = currentPage === item.pageIndex ? 'rgba(255,255,255,0.15)' : 'transparent')}
                    >
                        {item.title}
                    </div>
                    {item.children.length > 0 && renderTocItems(item.children, depth + 1)}
                </li>
            ))}
        </ul>
    );

    if (status === 'login') {
        return <Navigate to={`/${i18n.language}/login`} replace />;
    }
    if (status === 'trial') {
        return <Navigate to={`/${i18n.language}/dashboard/subscription`} replace state={{ trialBlocked: true }} />;
    }

    if (status === 'loading') {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 70px)', background: '#525659' }}>
                <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem' }}></div>
            </div>
        );
    }

    if (status === 'notfound' || !pdfUrl) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 70px)', background: '#525659', color: 'white' }}>
                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">{isAr ? 'الملف غير موجود' : 'Fichier introuvable'}</h5>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .rpv-core__viewer { height: calc(100vh - 70px) !important; }
                /* Theme: dark red toolbar */
                .rpv-toolbar { background: #6B0F1A !important; }
                .rpv-core__minimal-button { color: white !important; }
                .rpv-core__minimal-button:hover { background: rgba(255,255,255,0.2) !important; }
                .rpv-core__textbox { background: rgba(255,255,255,0.15) !important; color: white !important; border: 1px solid rgba(255,255,255,0.3) !important; }
                .rpv-page-navigation__current-page-input input { color: white !important; }
                /* Hide default sidebar entirely */
                .rpv-default-layout__sidebar { display: none !important; }
                .rpv-core__inner-page { background: #525659 !important; }
                /* Hide download-related buttons */
                [data-testid="get-file__download-button"],
                [data-testid="open__input-wrapper"] { display: none !important; }
            `}</style>
            <div style={{ display: 'flex', height: 'calc(100vh - 70px)' }}>
                {/* Custom static TOC */}
                {showToc && tocItems.length > 0 && (
                    <div style={{
                        width: '250px',
                        minWidth: '250px',
                        background: 'rgb(107, 15, 26)',
                        overflowY: 'auto',
                        padding: '8px 0',
                    }}>
                        {renderTocItems(tocItems)}
                    </div>
                )}
                {/* PDF Viewer */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js">
                        <Viewer
                            fileUrl={pdfUrl}
                            plugins={[defaultLayoutPluginInstance, searchPluginInstance, pageNavigationPluginInstance]}
                            defaultScale={0.75}
                            theme={{ direction: isAr ? 'rtl' : 'ltr' } as any}
                            onPageChange={(e) => setCurrentPage(e.currentPage)}
                        />
                    </Worker>
                </div>
            </div>
        </>
    );
};

export default PdfViewer;
